import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageTemplate } from '@/components/templates'
import { CONTAINER_SIZES } from '@/constants'
import { Button, Input } from '@/components/atoms'
import { Form } from '@/components/molecules'
import { dataRoomService } from '@/services/dataRoomService'
import { RFQRequest } from '@/types/project'
import { DollarSign, TrendingUp, Building2, Search } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { projectsKeys } from '@/hooks/useOTC'

const PlaceOrderScreen = () => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedProject, setSelectedProject] = useState<any>(null)
  const [formData, setFormData] = useState({
    tranche_size: '',
    valuation: '',
    type: 'BUY' as 'BUY' | 'SELL'
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  // Fetch projects for selection
  const { data: projectsData, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects', 'search', searchQuery],
    queryFn: () => dataRoomService.getFundingRounds({ search: searchQuery, limit: 10 }),
    enabled: searchQuery.length > 2
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedProject) {
      setError('Please select a project first')
      return
    }

    setLoading(true)
    setError('')

    try {
      const rfqData: RFQRequest = {
        project_id: selectedProject.project_id,
        tranche_size: parseFloat(formData.tranche_size.replace(/[^0-9.]/g, '')),
        type: formData.type,
        ...(formData.valuation ? { valuation: parseFloat(formData.valuation.replace(/[^0-9.]/g, '')) } : {})
      }

      await dataRoomService.createRFQ(rfqData)
      setSuccess(true)
      setTimeout(() => navigate('/trading/otc'), 2000)
    } catch (err) {
      setError('Failed to submit order. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <PageTemplate
      title="Place New Order"
      subtitle="Submit a Request for Quote (RFQ) for any project"
      maxWidth={CONTAINER_SIZES.NARROW}
    >
      <div className="bg-white dark:bg-[#1c1c1c] p-8 rounded-xl shadow-lg border border-border">
        {success ? (
          <div className="text-center py-12 space-y-4">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
              <Building2 size={40} className="text-green-500" />
            </div>
            <h2 className="text-2xl font-bold">Order Submitted!</h2>
            <p className="opacity-60 text-lg">Your RFQ has been successfully created. Redirecting to OTC market...</p>
          </div>
        ) : (
          <Form onSubmit={handleSubmit} className="space-y-8">
            {error && (
              <div className="bg-red-500/10 border border-red-500 text-red-500 p-4 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Project Selection */}
            <div className="space-y-4">
              <label className="block text-sm font-semibold uppercase tracking-wider opacity-70">
                1. Select Project
              </label>
              
              {!selectedProject ? (
                <div className="relative">
                  <Input
                    placeholder="Search for a project (e.g. Ethereum, Solana...)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    leftElement={<Search size={18} />}
                    className="h-12"
                  />
                  
                  {projectsData && projectsData.data && projectsData.data.length > 0 && searchQuery.length > 2 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-900 border border-border rounded-lg shadow-2xl z-50 overflow-hidden max-h-60 overflow-y-auto">
                      {projectsData.data.map((p: any) => (
                        <button
                          key={p.project_id}
                          type="button"
                          onClick={() => setSelectedProject(p)}
                          className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors border-b border-border last:border-0"
                        >
                          {p.logo ? (
                            <img src={p.logo} className="w-8 h-8 rounded-full" alt="" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs">
                              {p.project_name.charAt(0)}
                            </div>
                          )}
                          <span className="font-medium">{p.project_name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-between p-4 bg-primary/5 border border-primary/20 rounded-lg">
                  <div className="flex items-center gap-3">
                    {selectedProject.logo ? (
                      <img src={selectedProject.logo} className="w-10 h-10 rounded-full" alt="" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        {selectedProject.project_name.charAt(0)}
                      </div>
                    )}
                    <div>
                      <h3 className="font-bold text-lg">{selectedProject.project_name}</h3>
                      <p className="text-xs opacity-60">Selected Project</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedProject(null)}>
                    Change
                  </Button>
                </div>
              )}
            </div>

            {/* Order Details */}
            <div className="space-y-6">
              <label className="block text-sm font-semibold uppercase tracking-wider opacity-70">
                2. Order Details
              </label>

              <Form.Field label="Order Type" required>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, type: 'BUY' })}
                    className={`h-12 rounded-lg border-2 transition-all font-bold ${
                      formData.type === 'BUY' 
                        ? 'border-green-500 bg-green-500/10 text-green-600' 
                        : 'border-border opacity-60'
                    }`}
                  >
                    BUY
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, type: 'SELL' })}
                    className={`h-12 rounded-lg border-2 transition-all font-bold ${
                      formData.type === 'SELL' 
                        ? 'border-red-500 bg-red-500/10 text-red-600' 
                        : 'border-border opacity-60'
                    }`}
                  >
                    SELL
                  </button>
                </div>
              </Form.Field>

              <Form.Field label="Amount / Tranche Size ($)" required>
                <Input
                  type="text"
                  placeholder="e.g. 100,000"
                  value={formData.tranche_size}
                  onChange={(e) => setFormData({ ...formData, tranche_size: e.target.value })}
                  leftElement={<DollarSign size={18} />}
                  className="h-12"
                />
              </Form.Field>

              <Form.Field label="Desired Valuation ($) - Optional">
                <Input
                  type="text"
                  placeholder="e.g. 1,000,000,000"
                  value={formData.valuation}
                  onChange={(e) => setFormData({ ...formData, valuation: e.target.value })}
                  leftElement={<TrendingUp size={18} />}
                  className="h-12"
                />
              </Form.Field>
            </div>

            <div className="pt-4 flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="flex-1 h-12"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading || !selectedProject || !formData.tranche_size}
                isLoading={loading}
                className="flex-1 h-12 font-bold"
              >
                Create Order
              </Button>
            </div>
          </Form>
        )}
      </div>
    </PageTemplate>
  )
}

export default PlaceOrderScreen
